import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroVideos } from '@/mocks/videos';

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 500);
    },
    [isTransitioning],
  );

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % heroVideos.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const video = heroVideos[current];

  return (
    <section className="relative w-full h-[480px] md:h-[640px] overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center transition-all duration-700 ease-out"
        style={{ backgroundImage: `url(${video.poster})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-950/40"></div>
      </div>

      <div className="absolute inset-0 flex items-end">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pb-12 md:pb-16 w-full">
          <div
            key={video.id}
            className="animate-[fadeInUp_0.6s_ease-out] flex flex-col md:flex-row items-end md:items-center gap-6 md:gap-10"
          >
            <div className="hidden md:block w-[180px] md:w-[220px] h-[250px] md:h-[310px] rounded-lg overflow-hidden flex-shrink-0">
              <img
                src={video.poster}
                alt={video.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-accent-500 text-white text-xs font-semibold whitespace-nowrap">
                  {video.category}
                </span>
                <span className="text-sm text-foreground-200">{video.year}</span>
                <span className="flex items-center gap-1 text-accent-400 text-sm">
                  <i className="ri-star-fill"></i>
                  <span>{video.rating}</span>
                </span>
                <span className="text-sm text-foreground-200">{video.status}</span>
              </div>

              <h2 className="text-2xl md:text-4xl font-bold text-white mb-3 font-heading">
                {video.title}
              </h2>

              <p className="text-sm md:text-base text-foreground-300 leading-relaxed mb-4 line-clamp-2 max-w-2xl">
                {video.description}
              </p>

              <div className="flex items-center gap-3 flex-wrap">
                <Link
                  to={`/detail/${video.id}`}
                  className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-semibold transition-all whitespace-nowrap"
                >
                  <i className="ri-play-fill mr-1"></i>立即观看
                </Link>
                <Link
                  to={`/detail/${video.id}`}
                  className="px-6 py-2.5 border border-background-300 hover:border-foreground-300 text-foreground-200 hover:text-white rounded-full text-sm transition-all whitespace-nowrap"
                >
                  查看详情
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroVideos.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === current ? 'w-6 bg-primary-500' : 'bg-background-400 hover:bg-background-300'
            }`}
            aria-label={`切换到第${index + 1}张`}
          />
        ))}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}